import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useForgotPassword } from "../services/authentication";
import assetPath from "../utils/assetPath";
import ROUTES from "../utils/ROUTES";
import { ArrowRight, ShieldCheck, User as UserIcon } from "lucide-react";

const imgLogo = assetPath("/logo.png");

const schema = z.object({
  email: z.email("Please enter a valid email address"),
});

type ForgotFormData = z.infer<typeof schema>;

const ForgotAccess = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({ resolver: zodResolver(schema) });

  const { mutate, isPending, error, isSuccess } = useForgotPassword();

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-4 pt-8 pb-40 bg-brand-primary">
      {/* Logo */}
      <img
        src={imgLogo}
        alt="The Coconut Tree"
        className="mb-10 h-[85px] w-[249px] object-cover"
      />

      {/* Card */}
      <div className="relative w-full max-w-[452px] flex flex-col gap-10 rounded-[32px] border border-white/40 p-[41px] backdrop-blur-[6px] bg-surface-card shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]">
        {/* Heading */}
        <div className="flex flex-col gap-1 text-center">
          <h1 className="font-bebas text-2xl uppercase tracking-[0.48px] text-content-heading">
            Forgot Access
          </h1>
          <p className="font-montserrat text-xs font-medium text-content-label">
            Enter your email address and we'll send you a reset link.
          </p>
        </div>

        {isSuccess ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="font-montserrat text-sm font-medium text-brand-teal">
              Reset link sent! Check your inbox and follow the instructions.
            </p>
            <button
              type="button"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="font-montserrat text-xs font-medium hover:underline text-content-label"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit((data) => mutate(data.email))}
            noValidate
            className="flex flex-col gap-6"
          >
            {/* Email */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="email"
                className="font-montserrat px-1 text-xs font-medium text-content-label"
              >
                Email Address
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <UserIcon className="h-[13px] w-[13px]" />
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  className="font-montserrat w-full rounded-lg py-[18px] pl-10 pr-4 text-base font-medium outline-none placeholder:text-[#c2c7c9] bg-surface-input text-content-label"
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                {(error as Error).message}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isPending}
              className="font-montserrat flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-[14px] font-semibold text-white transition disabled:opacity-60 bg-gradient-to-br from-[#011b21] to-[#173036] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]"
            >
              {isPending ? "Sending…" : "Send Reset Link"}
              {!isPending && (
                <ArrowRight className="h-[13px] w-[13px]" />
              )}
            </button>

            {/* Back to login */}
            <button
              type="button"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="font-montserrat text-xs font-medium hover:underline text-center text-content-label"
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="border-t border-[rgba(194,199,201,0.2)] pt-8">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-[13px] w-[13px]" />
            <span className="font-montserrat text-xs font-medium text-content-label">
              End-to-end encrypted session
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotAccess;
